/**
 * Routes PubNub presence traffic into per-channel PresenceStores.
 *
 * Phase 1 of the realtime-layer ADR: the bridge only ADDS a listener to the
 * existing PubNub instance — channel subscriptions are still owned by the
 * legacy controllers (RoomsController/Messaging) and useActivityLiveEvent,
 * so presence events flow to the bridge because those subscriptions already
 * exist. Subscription ownership moves here in phase 2.
 */
import PubNub from 'pubnub';

import { realtimeLog } from './debug';
import { PresenceStore } from './PresenceStore';

type PresenceEvent = PubNub.Subscription.Presence;

const stores = new Map<string, PresenceStore>();
// hereNow requests in flight, by channel. The connect seed and a mounting
// usePresence often ask for the same channel at once; they share one request.
const pendingRefreshes = new Map<string, Promise<void>>();
let attachedTo: PubNub | null = null;
let attachedListener: {
  presence: (event: PresenceEvent) => void;
} | null = null;

const routePresenceEvent = (event: PresenceEvent) => {
  const store = stores.get(event.channel);
  const who = 'uuid' in event ? event.uuid : '';
  if (!store) {
    realtimeLog(
      `bridge: ${event.action} on ${event.channel} — no store, ignored`,
      who
    );
    return;
  }

  realtimeLog(
    `bridge: ${event.action} on ${event.channel}`,
    who,
    'occupancy' in event ? `(occupancy ${event.occupancy})` : ''
  );

  switch (event.action) {
    case 'join':
      store.applyJoin(event.uuid);
      break;
    case 'leave':
      store.applyLeave(event.uuid);
      break;
    case 'timeout':
      store.applyTimeout(event.uuid);
      break;
    case 'interval': {
      if (event.hereNowRefresh) {
        // The change was too large for the event payload, so PubNub sent the
        // flag instead of join/leave/timeout lists: read occupancy instead.
        // A quiet interval (no lists, no flag) means nothing changed.
        void refreshPresence(event.channel);
        break;
      }
      (event.join ?? []).forEach((uuid) => store.applyJoin(uuid));
      (event.leave ?? []).forEach((uuid) => store.applyLeave(uuid));
      (event.timeout ?? []).forEach((uuid) => store.applyTimeout(uuid));
      break;
    }
    default:
      break;
  }
};

/**
 * Attach the bridge listener to the current window.pubnub instance (the
 * alias realtimeClient assigns at init), re-attaching whenever the instance
 * changed. Returns false when no instance exists yet.
 */
function ensureAttached(): boolean {
  const pubnub = window.pubnub;
  if (!pubnub) {
    return false;
  }
  if (attachedTo === pubnub) {
    return true;
  }
  if (attachedTo && attachedListener) {
    attachedTo.removeListener(attachedListener);
  }
  attachedListener = { presence: routePresenceEvent };
  pubnub.addListener(attachedListener);
  attachedTo = pubnub;
  realtimeLog('bridge: attached to PubNub instance');
  return true;
}

/** Get (or lazily create) the presence store for a channel. */
export function getPresenceStore(channel: string): PresenceStore {
  let store = stores.get(channel);
  if (!store) {
    store = new PresenceStore({ label: channel });
    stores.set(channel, store);
  }
  ensureAttached();
  return store;
}

/**
 * Drop a channel's store once nothing needs it — called by channelManager
 * when a channel is unsubscribed (room switch, activity unmount) and by the
 * roster-edit poll when it stops. A store with live subscribers (a mounted
 * usePresence) is kept: disposing it would clear their listener and freeze
 * their view, and a fresh store on the next lookup would be one they never
 * see. Such stores are released by the next unsubscribe after they unmount.
 */
export function releasePresenceStore(channel: string) {
  const store = stores.get(channel);
  if (!store) {
    return;
  }
  if (store.hasListeners()) {
    realtimeLog(`bridge: ${channel} store still has subscribers, kept`);
    return;
  }
  store.dispose();
  stores.delete(channel);
  realtimeLog(`bridge: ${channel} store released`);
}

/** Seed/refresh a channel's store from authoritative hereNow occupancy. */
export function refreshPresence(channel: string): Promise<void> {
  if (!ensureAttached() || !window.pubnub) {
    return Promise.resolve();
  }
  const pending = pendingRefreshes.get(channel);
  if (pending) {
    return pending;
  }
  const request = fetchSnapshot(window.pubnub, channel).finally(() => {
    pendingRefreshes.delete(channel);
  });
  pendingRefreshes.set(channel, request);
  return request;
}

async function fetchSnapshot(pubnub: PubNub, channel: string): Promise<void> {
  try {
    const response = await pubnub.hereNow({
      channels: [channel],
      includeUUIDs: true,
    });
    const occupants = response.channels[channel]?.occupants ?? [];
    getPresenceStore(channel).applySnapshot(
      occupants.map((occupant) => occupant.uuid)
    );
  } catch (error) {
    // hereNow failures leave the store unseeded; consumers keep their
    // legacy fallback values, so this degrades to current behavior.
    realtimeLog(`bridge: hereNow FAILED for ${channel}`, error);
  }
}

/**
 * Batched occupancy counts for many channels in ONE hereNow transaction —
 * the Rooms-overview poll from the ADR (replaces per-room live
 * subscriptions for rooms the teacher is not in).
 */
export async function fetchOccupancy(
  channels: string[]
): Promise<Record<string, number>> {
  if (channels.length === 0 || !ensureAttached() || !window.pubnub) {
    return {};
  }
  const response = await window.pubnub.hereNow({ channels });
  const counts: Record<string, number> = {};
  channels.forEach((channel) => {
    counts[channel] = response.channels[channel]?.occupancy ?? 0;
  });
  realtimeLog('bridge: batched occupancy', counts);
  return counts;
}

/** Test/teardown hook: detach the listener and drop all stores. */
export function resetPresenceBridge() {
  if (attachedTo && attachedListener) {
    attachedTo.removeListener(attachedListener);
  }
  attachedTo = null;
  attachedListener = null;
  stores.forEach((store) => store.dispose());
  stores.clear();
  pendingRefreshes.clear();
}
